import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { themeClasses, Button } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';

function AdminLoginPage() {
  const { user, isAuthenticated, isLoading: authLoading, loginAsUser, salesforceAdminLogin } =
    useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAutoLoginFlow, setIsAutoLoginFlow] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const contactId = searchParams.get('contactId');
    const timestamp = searchParams.get('timestamp');

    if (token && contactId && timestamp) {
      setIsAutoLoginFlow(true);
      setIsLoading(true);
      salesforceAdminLogin(token, contactId, timestamp)
        .then(() => {
          navigate('/collection');
        })
        .catch((err) => {
          console.error('FHxD auto-login error:', err);
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to authenticate from Salesforce. The link may have expired.'
          );
          setIsLoading(false);
        });
    }
  }, [searchParams, salesforceAdminLogin, navigate]);

  const isManualFormAccess = !isAutoLoginFlow && !searchParams.get('token');

  if (authLoading) {
    return (
      <div className="fhxd-chrome-top min-h-[50vh] flex items-center justify-center">
        <p className={themeClasses.text.mutedSmall}>Loading...</p>
      </div>
    );
  }

  if (isManualFormAccess) {
    if (!isAuthenticated || !user) {
      return (
        <div className="fhxd-chrome-top">
          <section className="py-16 bg-theme-bg">
            <div className="max-w-md mx-auto px-4 text-center space-y-6">
              <h1 className={themeClasses.title.h3}>Authentication Required</h1>
              <p className={themeClasses.text.mutedSmall}>
                You must be logged in as Four Hundred staff to access this page.
              </p>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </section>
        </div>
      );
    }

    const domain = user.email.split('@')[1];
    if (domain !== 'fourhundred.com') {
      return (
        <div className="fhxd-chrome-top">
          <section className="py-16 bg-theme-bg">
            <div className="max-w-md mx-auto px-4 text-center space-y-6">
              <h1 className={themeClasses.title.h3}>Access Denied</h1>
              <p className={themeClasses.text.mutedSmall}>
                This feature is only available to Four Hundred staff members.
              </p>
              <Button variant="primary" onClick={() => navigate('/collection')}>
                Go to Collection
              </Button>
            </div>
          </section>
        </div>
      );
    }
  }

  if (isAuthenticated && user?.isAdminSession && isManualFormAccess) {
    return (
      <div className="fhxd-chrome-top">
        <section className="py-16 bg-theme-bg">
          <div className="max-w-md mx-auto px-4 text-center space-y-6">
            <h1 className={themeClasses.title.h3}>Already in Admin View</h1>
            <p className={themeClasses.text.mutedSmall}>
              You are currently viewing the portal as <strong>{user.name}</strong>. Please exit
              admin view before logging in as another advisor.
            </p>
            <Button variant="primary" onClick={() => navigate('/collection')}>
              Go to Collection
            </Button>
          </div>
        </section>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await loginAsUser(input);
      navigate('/collection');
    } catch (err) {
      console.error('FHxD admin login error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to login as user. Please check the input and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fhxd-chrome-top">
      <section className="relative py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/burj-2.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="font-title font-normal text-3xl md:text-5xl tracking-wider uppercase text-white mb-4">
            Admin Login as Advisor
          </h1>
          <p className="font-body text-white/90 max-w-2xl mx-auto">
            Enter a Lead ID, Contact ID, or paste the Salesforce Experience login URL
          </p>
        </div>
      </section>

      <section className="py-16 bg-theme-bg">
        <div className="max-w-lg mx-auto px-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="input" className={`block ${themeClasses.text.caption} mb-2`}>
                Lead ID, Contact ID, or Experience URL
              </label>
              <textarea
                id="input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                required
                rows={2}
                className={`${themeClasses.form.input} font-mono text-sm`}
                placeholder="Enter Lead ID (00Q...) or Contact ID (003...)"
                disabled={isLoading}
              />
              <p className={`${themeClasses.text.mutedSmall} mt-2`}>
                Easiest: paste the Lead ID (starts with 00Q) or Contact ID (starts with 003). Or
                paste the full Experience URL.
              </p>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !input.trim()}
              className="w-full"
            >
              {isLoading ? 'Logging in...' : 'Login as Advisor'}
            </Button>

            <div className="text-center">
              <Link to="/login" className={`${themeClasses.text.mutedSmall} underline`}>
                Back to regular login
              </Link>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default AdminLoginPage;
